/**
 * WeaveIn.ai Platform - Claims API Service
 * TypeScript service layer for healthcare claims management
 */

import axios, { AxiosInstance } from 'axios';

import { config as productConfig } from '@/src/config';
// ============================================================================
// TYPE DEFINITIONS
// ============================================================================

export interface ClaimLedgerEvent {
  event_date: string | null;
  event_type: string;
  description: string;
  line_number: number | null;
  reference_number: string | null;
  debit: number;
  credit: number;
  running_balance: number;
  actor: number | null;
  source: string;
}

export interface PendingBillableActivity {
  activity_id: number;
  enrollment_id: number;
  activity_name: string;
  billing_activity_id: number | null;
  activity_code: string | null;
  estimated_charge: number;
  program_name: string | null;
}

export interface ClaimLedger {
  claim_id: number;
  claim_number: string;
  member_id: number;
  member_name: string;
  total_charge: number;
  outstanding_ar: number;
  events: ClaimLedgerEvent[];
}

export interface Claim {
  id: number;
  claim_number: string;
  original_claim_id?: number;
  claim_frequency_code?: string;

  // Organization and program
  organization_id: number;
  program_id?: number;

  // Member/patient
  member_id: number;
  member_name?: string;

  // Payer
  payer_id: number;
  payer_name?: string;

  // Billing provider
  billing_provider_npi: string;
  billing_provider_ein?: string;
  billing_provider_name?: string;
  billing_provider_address?: Record<string, any>;

  // Rendering provider
  rendering_provider_npi?: string;
  rendering_provider_name?: string;

  // Service dates
  service_date_from: string;
  service_date_to: string;
  admission_date?: string;
  discharge_date?: string;

  // Place of service
  place_of_service?: string;
  facility_type_code?: string;

  // Diagnosis codes (ICD-10, including Z-codes)
  diagnosis_code_1?: string;
  diagnosis_code_2?: string;
  diagnosis_code_3?: string;
  diagnosis_code_4?: string;
  diagnosis_code_5?: string;
  diagnosis_code_6?: string;
  diagnosis_code_7?: string;
  diagnosis_code_8?: string;

  // Amounts
  total_charge: number;
  total_allowed: number;
  total_paid: number;
  total_adjustment: number;
  patient_responsibility: number;

  // Status
  claim_status: 'draft' | 'validated' | 'submitted' | 'accepted' | 'rejected' | 'denied' | 'partial_paid' | 'paid' | 'appealed';
  internal_status?: string;

  // Submission tracking
  submission_date?: string;
  submission_method?: string;
  clearinghouse_trace_number?: string;
  clearinghouse_status?: string;
  payer_claim_number?: string;

  // Response tracking
  acknowledgment_date?: string;
  adjudication_date?: string;
  payment_date?: string;
  check_eft_number?: string;

  // Prior authorization
  prior_auth_number?: string;
  referral_number?: string;

  // Notes
  internal_notes?: string;
  payer_notes?: string;

  // Audit
  created_ts: string;
  updated_ts?: string;
  created_by?: number;
  updated_by?: number;
  submitted_by?: number;

  // Line items (may be included in response)
  line_items?: ClaimLineItem[];
}

export interface ClaimLineItem {
  id: number;
  claim_id: number;
  line_number: number;

  // Link to billing activity
  billing_activity_id?: number;

  // Procedure codes
  cpt_code?: string;
  hcpcs_code?: string;
  revenue_code?: string;

  // Modifiers
  modifier_1?: string;
  modifier_2?: string;
  modifier_3?: string;
  modifier_4?: string;

  // Diagnosis pointers
  diagnosis_pointers?: string;

  // Service details
  service_date: string;
  place_of_service?: string;

  // Units and amounts
  units: number;
  charge_amount: number;
  allowed_amount: number;
  paid_amount: number;
  adjustment_amount: number;

  // Line status
  line_status: string;

  // Denial/adjustment codes
  adjustment_reason_code?: string;
  remark_codes?: string[];

  // Description
  service_description?: string;

  // Audit
  created_ts: string;
  updated_ts?: string;
}

export interface ClaimCreate {
  organization_id: number;
  program_id?: number;
  member_id: number;
  payer_id: number;

  billing_provider_npi: string;
  billing_provider_ein?: string;
  billing_provider_name?: string;

  rendering_provider_npi?: string;
  rendering_provider_name?: string;

  service_date_from: string;
  service_date_to: string;

  place_of_service?: string;

  // Diagnosis codes
  diagnosis_code_1?: string;
  diagnosis_code_2?: string;
  diagnosis_code_3?: string;
  diagnosis_code_4?: string;
  diagnosis_code_5?: string;
  diagnosis_code_6?: string;
  diagnosis_code_7?: string;
  diagnosis_code_8?: string;

  prior_auth_number?: string;
  referral_number?: string;

  internal_notes?: string;

  // Line items
  line_items: ClaimLineItemCreate[];
}

export interface ClaimLineItemCreate {
  line_number: number;
  billing_activity_id?: number;
  cpt_code?: string;
  hcpcs_code?: string;
  revenue_code?: string;
  modifier_1?: string;
  modifier_2?: string;
  modifier_3?: string;
  modifier_4?: string;
  diagnosis_pointers?: string;
  service_date: string;
  place_of_service?: string;
  units: number;
  charge_amount: number;
  service_description?: string;
}

export interface ClaimUpdate {
  claim_status?: string;
  internal_status?: string;
  internal_notes?: string;
  payer_notes?: string;
  prior_auth_number?: string;
  referral_number?: string;
}

export interface Payer {
  id: number;
  payer_name: string;
  payer_type: string;
  edi_payer_id?: string;
  submission_method: string;
  contact_name?: string;
  contact_email?: string;
  contact_phone?: string;
  portal_url?: string;
  is_active: boolean;
  created_ts: string;
  updated_ts?: string;
}

export interface ProviderEnrollment {
  id: number;
  organization_id: number;
  payer_id: number;
  payer_name?: string;
  provider_id_with_payer?: string;
  group_id_with_payer?: string;
  enrollment_status: string;
  enrollment_type?: string;
  application_date?: string;
  effective_date?: string;
  termination_date?: string;
  recredential_date?: string;
  contract_type?: string;
  network_status?: string;
  credentialing_status?: string;
  credentialing_notes?: string;
  created_ts: string;
  updated_ts?: string;
}

export interface ClaimDenial {
  id: number;
  claim_id: number;
  claim_line_item_id?: number;
  denial_date: string;
  denial_type: string;
  denial_reason_code: string;
  denial_reason_description?: string;
  remark_codes?: string[];
  group_code?: string;
  denied_amount: number;
  appeal_deadline?: string;
  timely_filing_deadline?: string;
  resolution_status: string;
  resolution_date?: string;
  resolution_amount?: number;
  resolution_notes?: string;
  created_ts: string;
  updated_ts?: string;
}

export interface DenialAppeal {
  id: number;
  denial_id: number;
  claim_id: number;
  appeal_number?: string;
  appeal_level: number;
  appeal_reason: string;
  supporting_documentation?: Record<string, any>;
  submitted_date?: string;
  submission_method?: string;
  appeal_status: string;
  payer_appeal_number?: string;
  decision_date?: string;
  decision_outcome?: string;
  decision_reason?: string;
  approved_amount?: number;
  can_escalate: boolean;
  escalation_deadline?: string;
  created_ts: string;
  updated_ts?: string;
}

export interface ClaimFilters {
  organization_id?: number;
  payer_id?: number;
  member_id?: number;
  program_id?: number;
  status?: string;
  service_date_from?: string;
  service_date_to?: string;
  submission_date_from?: string;
  submission_date_to?: string;
}

/** TRUS-722: GET /claims/{id}/ffs-readiness — may the claim be submitted, and why it is held. */
export interface ClaimReadinessFinding {
  rule: string;
  passed: boolean;
  detail: string;
  data?: Record<string, unknown>;
}

export interface ClaimReadiness {
  claim_id: number;
  ready: boolean;
  summary: string;
  held_reasons: string[];
  findings: ClaimReadinessFinding[];
  payer?: string | null;
  payer_type?: string | null;
  chi_codes_billed: boolean;
  rendering_provider_837: { loop_2310B: string; entity_type: string | null };
}

// ============================================================================
// API SERVICE CLASS
// ============================================================================

class ClaimsApiService {
  private client: AxiosInstance;

  constructor(baseURL: string) {
    this.client = axios.create({
      baseURL,
      headers: {
        'Content-Type': 'application/json',
      },
    });

    this.client.interceptors.request.use((config) => {
      const token = localStorage.getItem('auth_token');
      const cboId = localStorage.getItem('active_cbo_id');
      if (token) {
        config.headers.Authorization = `Bearer ${token}`;
      }
      if (cboId) {
        config.headers['X-CBO-ID'] = cboId;
      }
      return config;
    });
  }

  // ============================================================================
  // CLAIMS CRUD
  // ============================================================================

  /**
   * List claims with optional filters
   */
  async listClaims(filters?: ClaimFilters): Promise<Claim[]> {
    const params = new URLSearchParams();
    if (filters?.organization_id) params.append('organization_id', filters.organization_id.toString());
    if (filters?.payer_id) params.append('payer_id', filters.payer_id.toString());
    if (filters?.member_id) params.append('member_id', filters.member_id.toString());
    if (filters?.program_id) params.append('program_id', filters.program_id.toString());
    if (filters?.status) params.append('status', filters.status);
    if (filters?.service_date_from) params.append('service_date_from', filters.service_date_from);
    if (filters?.service_date_to) params.append('service_date_to', filters.service_date_to);

    const response = await this.client.get(`/claims?${params.toString()}`);
    return response.data;
  }

  /** TRUS-722: enrollment, CHW G0019/G0022 rules and 837 rendering provider for one claim. */
  async getClaimReadiness(claimId: number): Promise<ClaimReadiness> {
    const response = await this.client.get(`/claims/${claimId}/ffs-readiness`);
    return response.data;
  }

  /**
   * Get a single claim by ID
   */
  async getClaim(claimId: number): Promise<Claim> {
    const response = await this.client.get(`/claims/${claimId}`);
    return response.data;
  }

  /**
   * Create a new claim
   */
  async createClaim(claimData: ClaimCreate): Promise<Claim> {
    const response = await this.client.post('/claims', claimData);
    return response.data;
  }

  /**
   * Update an existing claim
   */
  async updateClaim(claimId: number, updates: ClaimUpdate): Promise<Claim> {
    const response = await this.client.put(`/claims/${claimId}`, updates);
    return response.data;
  }

  /**
   * Delete a claim (only if in draft status)
   */
  async deleteClaim(claimId: number): Promise<void> {
    await this.client.delete(`/claims/${claimId}`);
  }

  // ============================================================================
  // CLAIM SUBMISSION
  // ============================================================================

  /**
   * Submit a claim to clearinghouse/payer
   */
  async submitClaim(claimId: number): Promise<{ message: string; claim: Claim }> {
    const response = await this.client.post(`/claims/${claimId}/submit`);
    return response.data;
  }

  /**
   * Validate a claim before submission
   */
  async validateClaim(claimId: number): Promise<{ valid: boolean; errors?: string[] }> {
    const response = await this.client.post(`/claims/${claimId}/validate`);
    return response.data;
  }

  // ============================================================================
  // PAYERS
  // ============================================================================

  /**
   * List payers
   */
  async listPayers(payerType?: string): Promise<Payer[]> {
    const params = payerType ? `?payer_type=${payerType}` : '';
    const response = await this.client.get(`/claims/payers${params}`);
    return response.data;
  }

  /**
   * Get payer by ID
   */
  async getPayer(payerId: number): Promise<Payer> {
    const response = await this.client.get(`/claims/payers/${payerId}`);
    return response.data;
  }

  // ============================================================================
  // PROVIDER ENROLLMENTS
  // ============================================================================

  /**
   * List provider-payer enrollments
   */
  async listProviderEnrollments(organizationId?: number): Promise<ProviderEnrollment[]> {
    const params = organizationId ? `?organization_id=${organizationId}` : '';
    const response = await this.client.get(`/claims/provider-enrollments${params}`);
    return response.data;
  }

  // ============================================================================
  // DENIALS & APPEALS
  // ============================================================================

  /**
   * List denials for a claim
   */
  async listClaimDenials(claimId: number): Promise<ClaimDenial[]> {
    const response = await this.client.get(`/claims/${claimId}/denials`);
    return response.data;
  }

  /**
   * Create a denial appeal
   */
  async createAppeal(denialId: number, appealData: Partial<DenialAppeal>): Promise<DenialAppeal> {
    const response = await this.client.post(`/claims/denials/${denialId}/appeals`, appealData);
    return response.data;
  }

  /**
   * List appeals for a denial
   */
  async listAppeals(denialId: number): Promise<DenialAppeal[]> {
    const response = await this.client.get(`/claims/denials/${denialId}/appeals`);
    return response.data;
  }

  // ============================================================================
  // CLAIM LINE ITEMS
  // ============================================================================

  /**
   * Get line items for a claim
   */
  async getClaimLineItems(claimId: number): Promise<ClaimLineItem[]> {
    const response = await this.client.get(`/claims/${claimId}/line-items`);
    return response.data;
  }

  /**
   * Traceable line-item G/L ledger for a claim (full lifecycle, running balance).
   */
  async getClaimLedger(claimId: number): Promise<ClaimLedger> {
    const response = await this.client.get(`/claims/${claimId}/ledger`);
    return response.data;
  }

  /**
   * Documented billable activities for a member that are ready to bill.
   */
  async getMemberPendingBillableActivities(
    memberId: number
  ): Promise<{ member_id: number; activities: PendingBillableActivity[] }> {
    const response = await this.client.get(`/claims/member/${memberId}/pending-billable-activities`);
    return response.data;
  }

  /**
   * Draft an AI SOAP progress note from a telehealth transcript into the member's
   * pending progress-note form. Pass a real transcript, or omit for the demo one.
   */
  async draftProgressNote(
    memberId: number,
    transcript?: string
  ): Promise<{ member_id: number; form_instance_id: number | null; note: Record<string, any>; ai_source: string }> {
    const response = await this.client.post(`/claims/member/${memberId}/draft-progress-note`, { transcript });
    return response.data;
  }

  /** Reset the live billing demo so Complete → bill → remit can be re-run. */
  async resetBillingDemo(memberId: number): Promise<{ member_id: number; reset: Record<string, number> }> {
    const response = await this.client.post(`/claims/member/${memberId}/reset-billing-demo`);
    return response.data;
  }

  /**
   * Simulate the payer 835 remittance for a claim (demo): posts payment -> paid.
   */
  async postMockRemittance(claimId: number): Promise<{ ok: boolean; paid_amount?: number; era_control_number?: string }> {
    const response = await this.client.post(`/claims/${claimId}/mock-remittance`);
    return response.data;
  }

  /**
   * Add line item to existing claim
   */
  async addLineItem(claimId: number, lineItem: ClaimLineItemCreate): Promise<ClaimLineItem> {
    const response = await this.client.post(`/claims/${claimId}/line-items`, lineItem);
    return response.data;
  }

  /**
   * Update line item
   */
  async updateLineItem(
    claimId: number,
    lineItemId: number,
    updates: Partial<ClaimLineItemCreate>
  ): Promise<ClaimLineItem> {
    const response = await this.client.put(`/claims/${claimId}/line-items/${lineItemId}`, updates);
    return response.data;
  }

  /**
   * Delete line item
   */
  async deleteLineItem(claimId: number, lineItemId: number): Promise<void> {
    await this.client.delete(`/claims/${claimId}/line-items/${lineItemId}`);
  }
}

// ============================================================================
// EXPORT SERVICE INSTANCE
// ============================================================================

// Get the API base URL from Vite environment
const getApiBaseUrl = (): string => {
  return (productConfig.weaveinApi.baseUrl ?? 'https://weavein-api.onrender.com').trim() + '/api';
};

export const claimsApi = new ClaimsApiService(getApiBaseUrl());

export default claimsApi;
